import {
  Bell,
  BellOff,
  Check,
  CheckCheck,
  Mail,
  MessageSquare,
  Trash2,
  UserPlus,
  X,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { useCommentsStore } from '@/modules/sales/stores/comments.store'
import { useNotificationStore } from '@/shared/stores/notification.store'

function formatRelativeTime(dateString) {
  if (!dateString) return 'Just now'
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return 'Recently'

    const now = new Date()
    const diffInSeconds = Math.floor((now - date) / 1000)

    if (diffInSeconds < 60) return 'Just now'
    if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60)
      return `${minutes}m ago`
    }
    if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600)
      return `${hours}h ago`
    }
    const days = Math.floor(diffInSeconds / 86400)
    if (days < 7) return `${days}d ago`
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
  } catch {
    return 'Recently'
  }
}

function getNotificationIcon(type) {
  switch (type) {
    case 'comment':
    case 'comment_reminder':
      return {
        icon: MessageSquare,
        bg: 'bg-emerald-50 text-emerald-600 border-emerald-100',
        badge: 'Comment',
      }
    case 'lead':
    case 'lead_created':
      return {
        icon: UserPlus,
        bg: 'bg-indigo-50 text-indigo-600 border-indigo-100',
        badge: 'Lead',
      }
    case 'message':
      return {
        icon: Mail,
        bg: 'bg-violet-50 text-violet-600 border-violet-100',
        badge: 'Message',
      }
    default:
      return {
        icon: Bell,
        bg: 'bg-amber-50 text-amber-600 border-amber-100',
        badge: 'Alert',
      }
  }
}

export function NotificationDrawer({ open, onClose }) {
  const [activeTab, setActiveTab] = useState('unread')

  const notifications = useNotificationStore((state) => state.notifications)
  const unreadCount = useNotificationStore((state) => state.unreadCount)
  const markAsRead = useNotificationStore((state) => state.markAsRead)
  const markAllAsRead = useNotificationStore((state) => state.markAllAsRead)
  const deleteNotification = useNotificationStore(
    (state) => state.deleteNotification,
  )
  const clearNotifications = useNotificationStore(
    (state) => state.clearNotifications,
  )
  const fetchNotifications = useNotificationStore(
    (state) => state.fetchNotifications,
  )

  useEffect(() => {
    if (open) {
      fetchNotifications?.()
    }
  }, [open, fetchNotifications])

  const readCount = useMemo(
    () => notifications.filter((item) => item.read).length,
    [notifications],
  )

  // Handle ESC key to close
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && open) {
        onClose?.()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  // Prevent background scrolling when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  const handleMarkAsRead = (notification) => {
    markAsRead(notification.id)
    const commentId = notification.data?.commentId
    if (commentId) {
      useCommentsStore
        .getState()
        .markNotificationsAsRead([commentId])
        .catch(() => {})
    }
  }

  const handleMarkAllAsRead = () => {
    const commentIds = notifications
      .filter((n) => !n.read && n.data?.commentId)
      .map((n) => n.data.commentId)

    markAllAsRead()

    if (commentIds.length > 0) {
      useCommentsStore
        .getState()
        .markNotificationsAsRead(commentIds)
        .catch(() => {})
    }
  }

  const filteredNotifications = useMemo(() => {
    if (activeTab === 'unread') {
      return notifications.filter((item) => !item.read)
    }
    return notifications.filter((item) => item.read)
  }, [notifications, activeTab])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-[2px] transition-opacity duration-300 animate-in fade-in"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Right Drawer Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Notifications Drawer"
        className="relative z-10 flex h-full w-full max-w-md flex-col bg-white border-l border-slate-200 shadow-2xl transition-transform duration-300 ease-out animate-in slide-in-from-right"
      >
        {/* Drawer Header */}
        <div className="flex items-center justify-between border-b border-slate-100 bg-white px-5 py-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
              <Bell className="size-4.5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold text-slate-900">
                  Notifications
                </h2>
                {unreadCount > 0 && (
                  <span className="flex items-center rounded-full bg-red-500 px-2 py-0.5 text-[11px] font-bold text-white shadow-xs">
                    {unreadCount} unread
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500">
                Manage your real-time alerts and activity
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
            aria-label="Close notifications"
          >
            <X className="size-4.5" />
          </button>
        </div>

        {/* Drawer Toolbar & Filter Tabs (Only 2 Sections: Unread and Read) */}
        <div className="flex flex-col gap-2.5 border-b border-slate-100 bg-slate-50/70 px-5 py-3">
          <div className="flex items-center justify-between">
            {/* 2 Filter Tabs: Unread and Read */}
            <div className="flex items-center gap-1.5 rounded-lg bg-slate-200/60 p-1">
              <button
                type="button"
                onClick={() => setActiveTab('unread')}
                className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-all ${
                  activeTab === 'unread'
                    ? 'bg-white text-indigo-600 shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <span>Unread</span>
                <span
                  className={`rounded-full px-1.5 py-0.2 text-[10px] font-bold ${
                    activeTab === 'unread'
                      ? 'bg-indigo-100 text-indigo-700'
                      : 'bg-slate-300/70 text-slate-700'
                  }`}
                >
                  {unreadCount}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('read')}
                className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-all ${
                  activeTab === 'read'
                    ? 'bg-white text-indigo-600 shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <span>Read</span>
                <span
                  className={`rounded-full px-1.5 py-0.2 text-[10px] font-bold ${
                    activeTab === 'read'
                      ? 'bg-indigo-100 text-indigo-700'
                      : 'bg-slate-300/70 text-slate-700'
                  }`}
                >
                  {readCount}
                </span>
              </button>
            </div>

            {/* Quick Actions */}
            <div className="flex items-center gap-1.5 shrink-0">
              {unreadCount > 0 && activeTab === 'unread' && (
                <button
                  type="button"
                  onClick={handleMarkAllAsRead}
                  className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-semibold text-indigo-600 hover:bg-indigo-50 transition-colors"
                  title="Mark all as read"
                >
                  <CheckCheck className="size-3.5" />
                  <span>Mark all read</span>
                </button>
              )}

              {notifications.length > 0 && (
                <button
                  type="button"
                  onClick={clearNotifications}
                  className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-semibold text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors"
                  title="Clear all notifications"
                >
                  <Trash2 className="size-3.5" />
                  <span>Clear all</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto divide-y divide-slate-100 bg-white p-2">
          {filteredNotifications.length === 0 ? (
            <div className="flex h-full min-h-[300px] flex-col items-center justify-center p-6 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 text-slate-400 shadow-inner">
                <BellOff className="size-8" />
              </div>
              <h3 className="mt-4 text-sm font-semibold text-slate-800">
                No notifications found
              </h3>
              <p className="mt-1 text-xs text-slate-500 max-w-[240px]">
                {activeTab === 'unread'
                  ? "You've read all your notifications. Good job!"
                  : "When you receive lead updates, comments, or alerts, they'll appear right here."}
              </p>
            </div>
          ) : (
            filteredNotifications.map((notification) => {
              const {
                icon: TypeIcon,
                bg,
                badge,
              } = getNotificationIcon(notification.type)
              const isUnread = !notification.read

              return (
                <div
                  key={notification.id}
                  onClick={() => {
                    if (isUnread) handleMarkAsRead(notification)
                  }}
                  className={`group relative flex items-start gap-3.5 rounded-xl p-3.5 transition-all cursor-pointer ${
                    isUnread
                      ? 'bg-indigo-50/40 hover:bg-indigo-50/70 border border-indigo-100/60'
                      : 'bg-white hover:bg-slate-50'
                  }`}
                >
                  {/* Icon Indicator */}
                  <div
                    className={`relative mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-xl border ${bg} shadow-xs`}
                  >
                    <TypeIcon className="size-4.5" />
                    {isUnread && (
                      <span className="absolute -top-0.5 -right-0.5 size-2.5 rounded-full bg-indigo-600 ring-2 ring-white" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-bold text-slate-900 line-clamp-1">
                          {notification.title}
                        </span>
                        <span className="rounded-md bg-slate-100 px-1.5 py-0.2 text-[9px] font-semibold text-slate-600 uppercase tracking-wider">
                          {badge}
                        </span>
                      </div>

                      <span className="shrink-0 text-[10px] text-slate-400">
                        {formatRelativeTime(notification.createdAt)}
                      </span>
                    </div>

                    <p className="mt-1 text-xs text-slate-600 leading-relaxed break-words">
                      {notification.body}
                    </p>

                    {/* Footer Actions on hover */}
                    <div className="mt-2 flex items-center justify-end gap-1.5 opacity-80 group-hover:opacity-100 transition-opacity">
                      {isUnread && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleMarkAsRead(notification)
                          }}
                          className="inline-flex items-center gap-1 rounded-md bg-white border border-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-700 hover:bg-slate-100 transition-colors"
                        >
                          <Check className="size-3 text-indigo-500" />
                          Mark read
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          deleteNotification(notification.id)
                        }}
                        className="flex size-6 items-center justify-center rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                        title="Delete notification"
                      >
                        <Trash2 className="size-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
